// pages/CampaignDetail.js
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { 
  Box, Grid, Card, Stack, Container, Typography, Tab, Chip,
  Avatar, LinearProgress, Alert, List, ListItem, ListItemAvatar,
  ListItemText, Divider, ImageList, ImageListItem, Button,
  Modal, Fade, Backdrop, TextField, CircularProgress,
  FormControl, RadioGroup, FormControlLabel, Radio, Dialog, DialogTitle, DialogContent, DialogActions, Paper,
  alpha, IconButton
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import { CustomTabs } from '../components/custom-tabs';
import { Iconify } from '../components/iconify';
import { bgGradient } from '../theme/core/mixins/background';
import { fetchCampaignDetail } from '../store/startup/campaignDetail/campaignDetailSlice';
import {getFileLink} from '../utils/getFileLink';
import { ApiHelper } from '../utils/apiHelper';
import { toast } from 'react-toastify';
import { CheckCircle } from '@mui/icons-material';

const TABS = [
  { value: 'overview', label: 'Genel Bakış', icon: 'solar:home-linear' },
  { value: 'team', label: 'Takım', icon: 'solar:users-group-rounded-linear' },
  { value: 'product', label: 'Ürün', icon: 'solar:box-linear' },
  { value: 'risks', label: 'Riskler', icon: 'solar:shield-warning-linear' },
  { value: 'financials', label: 'Finansallar', icon: 'solar:chart-linear' },
  { value: 'documents', label: 'Dökümanlar', icon: 'solar:file-linear' },
  { value: 'media', label: 'Medya', icon: 'solar:gallery-linear' },
];

export default function CampaignDetail() {
  const [currentTab, setCurrentTab] = useState('overview');
  const dispatch = useDispatch();
  const { startupId } = useParams();
  const { data, loading, error } = useSelector((state) => state.campaignDetail);
  const [openInvestModal, setOpenInvestModal] = useState(false);
  const [investAmount, setInvestAmount] = useState('');
  const [investLoading, setInvestLoading] = useState(false);
  const [investError, setInvestError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [paymentDetail, setPaymentDetail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (startupId) {
      dispatch(fetchCampaignDetail(startupId));
    }
  }, [dispatch, startupId]);

  if (loading) {
    return (
      <Container>
        <LinearProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // Data henüz yüklenmediyse erken return
  if (!data) {
    return null;
  }

  const {
    profile,
    teamMembers,
    company,
    product,
    risks,
    analysis,
    funding,
    documents,
    balance,
    media
  } = data;

  const handleInvestModalOpen = () => {
    setOpenInvestModal(true);
    setInvestError('');
    setInvestAmount('');
  };

  const handleInvestModalClose = () => {
    setOpenInvestModal(false);
    setInvestError('');
    setInvestAmount('');
  };

  const handleInvestment = async () => {
    if (!investAmount || !paymentMethod) {
      toast.error("Lütfen tüm alanları doldurun");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await ApiHelper.post('/investment', {
        startupId,
        paymentType: paymentMethod,
        amount: parseFloat(investAmount)
      });

      if (response.data.code === "00000") {
        if (paymentMethod === 'BANK_TRANSFER') {
          setPaymentDetail(response.data.investment.paymentDetail);
          setSuccessDialogOpen(true);
        } else {
          toast.success("Yatırım başarıyla yapıldı!");
        }
        handleInvestModalClose();
        dispatch(fetchCampaignDetail(startupId));
      } else {
        setInvestError(response.data.message || "Yatırım yapılırken bir hata oluştu!");
      }
    } catch (error) {
      setInvestError(error.response?.data?.message || "Yatırım yapılırken bir hata oluştu!");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTeamTab = () => (
    <Grid container spacing={3}>
      {teamMembers?.map((member) => (
        <Grid item xs={12} md={4} key={member.teamMemberId}>
          <Card sx={{ p: 3 }}>
            <Stack spacing={2} alignItems="center">
              <Avatar 
                src={getFileLink(member.profilePhotoPath)} 
                sx={{ width: 96, height: 96 }}
              />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6">
                  {member.firstName} {member.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {member.positionTitle}
                </Typography>
              </Box>
              <Typography variant="body2">
                <div dangerouslySetInnerHTML={{ __html: member.biography }} />
                </Typography>
            </Stack>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderProductTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Proje Detayları</Typography>
          <Stack spacing={2}>
            <div dangerouslySetInnerHTML={{ __html: product?.project?.summary }} />
            <Typography variant="subtitle1">Problem</Typography>
            <div dangerouslySetInnerHTML={{ __html: product?.project?.problemDescription }} />
            <Typography variant="subtitle1">Çözüm</Typography>
            <div dangerouslySetInnerHTML={{ __html: product?.project?.solutionDescription }} />
          </Stack>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Geliştirme Süreci</Typography>
          <Stack spacing={2}>
            <div dangerouslySetInnerHTML={{ __html: product?.process?.developmentProcesses }} />
            <Typography variant="subtitle1">Ar-Ge Faaliyetleri</Typography>
            <div dangerouslySetInnerHTML={{ __html: product?.process?.rAndDActivities }} />
          </Stack>
        </Card>
      </Grid>
    </Grid>
  );

  const renderRisksTab = () => (
    <Grid container spacing={3}>
      {risks?.map((risk) => (
        <Grid item xs={12} md={6} key={risk.startupRiskId}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Risk Tipi {risk.riskType}
            </Typography>
            <div dangerouslySetInnerHTML={{ __html: risk.description }} />
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const documentBoxStyles = {
    p: 2, 
    border: 1, 
    borderColor: (theme) => theme.palette.divider,
    borderRadius: 1,
    bgcolor: (theme) => theme.palette.background.neutral,
    '&:hover': { 
      bgcolor: (theme) => theme.palette.action.hover,
      borderColor: (theme) => theme.palette.primary.main,
    }
  };

  const renderDocumentsTab = () => (
    <Grid container spacing={3}>
      {/* İzinler/Onaylar */}
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>İzinler ve Onaylar</Typography>
          <Stack spacing={2}>
            {documents?.permissions?.map((doc) => (
              <Box 
                key={doc.permissionsApprovalId}
                sx={documentBoxStyles}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Iconify icon="solar:file-pdf-linear" sx={{ width: 40, height: 40 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">{doc.subject}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {doc.description}
                    </Typography>
                  </Box>
                  <Iconify 
                    icon="solar:download-linear" 
                    sx={{ 
                      width: 24, 
                      height: 24,
                      cursor: 'pointer',
                      '&:hover': { color: 'primary.main' }
                    }} 
                    onClick={() => window.open(getFileLink(doc.documentFilePath), '_blank')}
                  />
                </Stack>
              </Box>
            ))}
          </Stack>
        </Card>
      </Grid>

      {/* Ödüller */}
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>Ödüller ve Başarılar</Typography>
          <Stack spacing={2}>
            {documents?.awards?.map((award) => (
              <Box 
                key={award.awardDocumentId}
                sx={documentBoxStyles}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Iconify icon="solar:medal-ribbons-star-linear" sx={{ width: 40, height: 40 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">{award.awardTitle}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {award.awardingEntity} - {award.date}
                    </Typography>
                  </Box>
                  {award.documentFilePath && (
                    <Iconify 
                      icon="solar:download-linear" 
                      sx={{ 
                        width: 24, 
                        height: 24,
                        cursor: 'pointer',
                        '&:hover': { color: 'primary.main' }
                      }} 
                      onClick={() => window.open(getFileLink(award.documentFilePath), '_blank')}
                    />
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Card>
      </Grid>

      {/* Analizler */}
      <Grid item xs={12}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>Analiz Dökümanları</Typography>
          <Grid container spacing={2}>
            {documents?.analysis?.[0] && (
              <>
                {documents.analysis[0].swotAnalysisPath && (
                  <Grid item xs={12} md={4}>
                    <Box 
                      sx={documentBoxStyles}
                      onClick={() => window.open(getFileLink(documents.analysis[0].swotAnalysisPath), '_blank')}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Iconify icon="solar:file-pdf-linear" sx={{ width: 32, height: 32 }} />
                        <Typography variant="subtitle2">SWOT Analizi</Typography>
                      </Stack>
                    </Box>
                  </Grid>
                )}
                
                {documents.analysis[0].businessPlanPath && (
                  <Grid item xs={12} md={4}>
                    <Box 
                      sx={documentBoxStyles}
                      onClick={() => window.open(getFileLink(documents.analysis[0].businessPlanPath), '_blank')}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Iconify icon="solar:file-pdf-linear" sx={{ width: 32, height: 32 }} />
                        <Typography variant="subtitle2">İş Planı</Typography>
                      </Stack>
                    </Box>
                  </Grid>
                )}

                {documents.analysis[0].investorPresentationPath && (
                  <Grid item xs={12} md={4}>
                    <Box 
                      sx={documentBoxStyles}
                      onClick={() => window.open(getFileLink(documents.analysis[0].investorPresentationPath), '_blank')}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Iconify icon="solar:file-pdf-linear" sx={{ width: 32, height: 32 }} />
                        <Typography variant="subtitle2">Yatırımcı Sunumu</Typography>
                      </Stack>
                    </Box>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );

  const mediaItemStyles = {
    aspectRatio: '1',
    '&:hover img': {
      transform: 'scale(1.1)',
      transition: 'transform 0.3s ease-in-out'
    },
    '& img': {
      borderRadius: 1,
      objectFit: 'contain',
      bgcolor: (theme) => theme.palette.background.neutral,
      padding: 1,
      transition: 'all 0.3s ease-in-out'
    }
  };

  const renderMediaTab = () => (
    <Grid container spacing={3}>
      {/* Fotoğraf Galerisi */}
      <Grid item xs={12}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>Fotoğraf Galerisi</Typography>
          <Grid container spacing={2}>
            {media?.filter(item => item.mediaType === 2)?.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.startupMediaId}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '300px',
                    cursor: 'pointer',
                    overflow: 'hidden', 
                    borderRadius: 2,
                    bgcolor: 'background.neutral',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover img': {
                      transform: 'scale(1.1)',
                    }
                  }}
                  onClick={() => window.open(getFileLink(item.mediaPath), '_blank')}
                >
                  <Box
                    component="img"
                    src={getFileLink(item.mediaPath)}
                    alt=""
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      padding: '8px',
                      transition: 'transform .3s ease',
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Card>
      </Grid>

      {/* Logo ve Görseller */}
      <Grid item xs={12}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>Logo ve Marka Görselleri</Typography>
          <Grid container spacing={2}>
            {media?.filter(item => item.mediaType === 1)?.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.startupMediaId}>
                <Box
                  sx={{
                    position: 'relative',
                    paddingTop: '100%', // 1:1 aspect ratio
                    width: '100%',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    borderRadius: 2,
                    bgcolor: 'background.neutral',
                    '&:hover img': {
                      transform: 'scale(1.1)',
                    }
                  }}
                  onClick={() => window.open(getFileLink(item.mediaPath), '_blank')}
                >
                  <Box
                    component="img"
                    src={getFileLink(item.mediaPath)}
                    alt=""
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform .3s ease',
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );

  const renderFinancialsTab = () => (
    <Grid container spacing={3}>
      {/* Finansal Görünüm */}
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>Finansal Görünüm</Typography>
          <Stack spacing={3}>
            <Box>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">Şirket Değerlemesi</Typography>
                  <Typography variant="h6">
                    {Number(funding?.details?.valuation || 0).toLocaleString('tr-TR')} ₺
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">Talep Edilen Yatırım</Typography>
                  <Typography variant="h6">
                    {Number(funding?.details?.fundingNeeded || 0).toLocaleString('tr-TR')} ₺
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">Yatırım Vadesi</Typography>
                  <Typography variant="h6">
                    {funding?.details?.fundingDurationMonths || 0} Ay
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider />

            {/* Özsermaye ve Pay Bilgileri */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Özsermaye ve Pay Bilgileri</Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Önerilen Hisse Oranı
                  </Typography>
                  <Typography variant="subtitle2">
                    %{funding?.details?.equityOffered || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Toplam Sermaye
                  </Typography>
                  <Typography variant="subtitle2">
                    {Number(funding?.postStartup?.totalCapital || 0).toLocaleString('tr-TR')} ₺
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Pay Birim Fiyatı
                  </Typography>
                  <Typography variant="subtitle2">
                    {Number(funding?.postStartup?.shareUnitPrice || 0).toLocaleString('tr-TR')} ₺
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Card>
      </Grid>

      {/* Kampanya Durumu */}
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>Kampanya Durumu</Typography>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Fonlama İlerlemesi</Typography>
              <LinearProgress 
                variant="determinate"
                value={((balance?.qualifiedAmount || 0) / (balance?.totalLimit || 1)) * 100}
                sx={{ height: 8, borderRadius: 1 }}
              />
              <Stack 
                direction="row" 
                justifyContent="space-between" 
                sx={{ mt: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  {Number(balance?.qualifiedAmount || 0).toLocaleString('tr-TR')} ₺
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {Number(balance?.totalLimit || 0).toLocaleString('tr-TR')} ₺
                </Typography>
              </Stack>
            </Box>

            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Hedeflenen Fon Tutarı
                </Typography>
                <Typography variant="subtitle2">
                  {Number(balance?.availableLimit || 0).toLocaleString('tr-TR')} ₺
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Tamamlanma Oranı
                </Typography>
                <Typography variant="subtitle2" color="primary.main">
                  %{balance?.qualifiedRate || 0}
                </Typography>
              </Box>
            </Stack>

            {funding?.generalJustification?.description && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Finansman Gerekçesi</Typography>
                <Typography variant="body2" color="text.secondary">
                  <div dangerouslySetInnerHTML={{ 
                    __html: funding.generalJustification.description 
                  }} />
                </Typography>
              </Box>
            )}
          </Stack>
        </Card>
      </Grid>
    </Grid>
  );

  // Hero bölümüne ekstra stil ve animasyon
  const heroSection = (
    <Box
      sx={{
        pt: 8,
        pb: 12,
        mt: -3,
        animation: 'fadeIn 1s ease-in-out',
        '@keyframes fadeIn': {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 },
        },
        boxShadow: (theme) => theme.customShadows.z24
      }}
    >
      <Container>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Typography
                variant="h2"
                sx={{
                  color: (theme) => theme.palette.text.primary
                }}
              >
                {profile?.name}
              </Typography>
              
              <Typography
                variant="h5"
                sx={{
                  color: (theme) => theme.palette.text.primary,
                  opacity: 0.72
                }}
              >
                {profile?.promotionName}
              </Typography>

              <Stack direction="row" spacing={1}>
                {profile?.mainCategories?.map((category) => ( // Optional chaining ekledik
                  <Chip 
                    key={category}
                    label={category} 
                    color="primary"
                    sx={{ borderRadius: 1 }}
                  />
                ))}
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, boxShadow: (theme) => theme.customShadows.z20 }}>
              <Stack spacing={2} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2" color="text.primary">Toplam Limit</Typography>
                  <Typography variant="subtitle1" color="text.primary">
                    {Number(balance?.totalLimit || 0).toLocaleString('tr-TR')} ₺
                  </Typography>
                </Box>

                <LinearProgress 
                  variant="determinate"
                  value={((balance?.qualifiedAmount || 0) / (balance?.totalLimit || 1)) * 100}
                  color="primary"
                />

                <Stack direction="row" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Kalan
                    </Typography>
                    <Typography variant="subtitle2" color="text.primary">
                      {Number(balance?.availableLimit || 0).toLocaleString('tr-TR')} ₺
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Tamamlanma
                    </Typography>
                    <Typography variant="subtitle2" color="text.primary">
                      %{balance?.qualifiedRate || 0}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ alignSelf: 'flex-end', mt: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleInvestModalOpen}
                    disabled={loading}
                  >
                    Yatırım Yap
                  </Button>
                  <IconButton
                    color="secondary"
                    onClick={() => alert("Kampanyayı Paylaş!")}
                  >
                    <ShareIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );

  // Tab içeriklerini saracak animasyon örneği (opsiyonel)
  const fadeStyles = {
    animation: 'fadeTabs 0.5s ease-in-out',
    '@keyframes fadeTabs': {
      '0%': { opacity: 0 },
      '100%': { opacity: 1 },
    }
  };

  // Modern kart stili
  const modernCardStyles = {
    p: 3,
    boxShadow: (theme) => theme.customShadows.z16,
    borderRadius: 2,
    transition: 'transform 0.3s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: (theme) => theme.customShadows.z24
    }
  };

  return (
    <Container maxWidth="xl">
      <Grid container spacing={3}>
        {/* Sol Taraf - Kampanya Bilgileri */}
        <Grid item xs={12} md={3}>
          <Card sx={{ p: 3, height: '100%' }}>
            <Stack spacing={3}>
              {/* Logo ve Şirket Adı */}
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={getFileLink(profile?.mainLogoPath)} 
                  alt={profile?.name}
                  style={{
                    width: '100%',
                    maxWidth: '200px',
                    height: 'auto',
                    marginBottom: '16px'
                  }}
                />
                <Typography variant="h6" gutterBottom>
                  {profile?.name}
                </Typography>
              </Box>

              {/* Kampanya Başlığı */}
              <Typography variant="body1" color="text.secondary">
                {profile?.promotionName}
              </Typography>

              {/* Kategori Bilgileri */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Kategoriler
                </Typography>
                <Stack direction="row" flexWrap="wrap" spacing={1}>
                  {profile?.mainCategories?.map((category) => (
                    <Chip 
                      key={category}
                      label={category} 
                      size="small"
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Stack>
              </Box>

              {/* Sektör Bilgileri */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Sektörler
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Kozmetik, Petrol, Kimya, Sağlık, Tıbbi Malzeme ve Savunma Sanayi
                </Typography>
              </Box>

              {/* İş Modeli */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  İş Modeli
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  B2B, B2C, C2C, B2E ve B2G
                </Typography>
              </Box>

              {/* İletişim Bilgileri */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  İletişim
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Nogayt Endüstri Kimya Arge İç ve Dış Ticaret Anonim Şirketi
                </Typography>
              </Box>

              {/* Sosyal Medya Linkleri */}
              <Stack direction="row" spacing={2} justifyContent="center">
                <IconButton>
                  <Iconify icon="mdi:whatsapp" />
                </IconButton>
                <IconButton>
                  <Iconify icon="mdi:linkedin" />
                </IconButton>
                <IconButton>
                  <Iconify icon="mdi:facebook" />
                </IconButton>
                <IconButton>
                  <Iconify icon="mdi:email" />
                </IconButton>
              </Stack>
            </Stack>
          </Card>
        </Grid>

        {/* Sağ Taraf - Ana İçerik */}
        <Grid item xs={12} md={9}>
          {heroSection}
          {/* Tabs Section */}
          <Container sx={{ mt: -8 }}>
            <Card>
              <CustomTabs
                value={currentTab}
                onChange={(event, newValue) => setCurrentTab(newValue)}
                sx={{ px: 3, bgcolor: 'transparent' }}
              >
                {TABS.map((tab) => (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    icon={<Iconify icon={tab.icon} sx={{ width: 24 }} />}
                    label={tab.label}
                  />
                ))}
              </CustomTabs>

              <Box sx={{ p: 3 }}>
                {/* Tab içerikleri burada render edilecek */}
                {currentTab === 'overview' && (
                  <Box sx={fadeStyles}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={8}>
                        <Stack spacing={3}>
                          <Typography variant="h6" color="text.primary">Hakkında</Typography>
                          <Typography color="text.primary">
                            <div dangerouslySetInnerHTML={{ __html: profile?.aboutStartup }} /> {/* Optional chaining ekledik */}
                          </Typography>
                          
                          <Box>
                            <Typography variant="h6" sx={{ mb: 2 }} color="text.primary">
                              Lokasyonlar
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" spacing={1}>
                              {profile?.locations?.map((location) => ( // Optional chaining ekledik
                                <Chip 
                                  key={location}
                                  label={location}
                                  variant="soft"
                                  size="small"
                                />
                              ))}
                            </Stack>
                          </Box>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Card sx={{ p: 3 }}>
                          <Stack spacing={2}>
                            <Typography variant="h6" color="text.primary">Yatırım Detayları</Typography>
                            
                            <Stack spacing={1}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                  Değerleme
                                </Typography>
                                <Typography variant="subtitle2" color="text.primary">
                                  {Number(funding?.details?.valuation || 0).toLocaleString('tr-TR')} ₺
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                  Hedeflenen Fon
                                </Typography>
                                <Typography variant="subtitle2" color="text.primary">
                                  {Number(funding?.details?.fundingNeeded || 0).toLocaleString('tr-TR')} ₺
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                  Önerilen Hisse
                                </Typography>
                                <Typography variant="subtitle2" color="text.primary">
                                  %{funding?.details?.equityOffered || 0}
                                </Typography>
                              </Box>
                            </Stack>
                          </Stack>
                        </Card>
                      </Grid>
                    </Grid>
                    <Card sx={{ ...modernCardStyles, mt: 3 }}>
                      <Typography variant="h5" sx={{ mb: 2 }}>
                        Neden Bu Kampanyaya Katılmalısınız?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Yüksek potansiyele sahip bu girişime erken dönemde katılarak 
                        hem geleceğe yatırım yapacak hem de değişimin parçası olacaksınız.
                      </Typography>
                    </Card>
                  </Box>
                )}
                {currentTab === 'team' && renderTeamTab()}
                {currentTab === 'product' && renderProductTab()}
                {currentTab === 'risks' && renderRisksTab()}
                {currentTab === 'financials' && renderFinancialsTab()}
                {currentTab === 'documents' && renderDocumentsTab()}
                {currentTab === 'media' && renderMediaTab()}
              </Box>
            </Card>
          </Container>
          <Modal
            open={openInvestModal}
            onClose={handleInvestModalClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
              backdrop: {
                timeout: 500,
              },
            }}
          >
            <Fade in={openInvestModal}>
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 400,
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 24,
                p: 4,
              }}>
                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                  Yatırım Yap
                </Typography>
                
                <TextField
                  fullWidth
                  label="Yatırım Tutarı (₺)"
                  type="number"
                  value={investAmount}
                  onChange={(e) => setInvestAmount(e.target.value)}
                  error={!!investError}
                  helperText={investError}
                  sx={{ mb: 3 }}
                />

                <FormControl component="fieldset" fullWidth sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Ödeme Yöntemi
                  </Typography>
                  <RadioGroup
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <FormControlLabel
                      value="BANK_TRANSFER"
                      control={<Radio />}
                      label="Banka Transferi"
                      disabled={isSubmitting}
                    />
                    <FormControlLabel
                      value="CREDIT_CARD"
                      control={<Radio />}
                      label="Kredi Kartı"
                      disabled={isSubmitting}
                    />
                  </RadioGroup>
                </FormControl>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                  <Button onClick={handleInvestModalClose} disabled={isSubmitting}>
                    İptal
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleInvestment}
                    disabled={!investAmount || !paymentMethod || isSubmitting}
                  >
                    {isSubmitting ? <CircularProgress size={24} /> : 'Yatırım Yap'}
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Modal>
          <Dialog 
            open={successDialogOpen} 
            onClose={() => setSuccessDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ 
              pb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <CheckCircle color="success" />
              Yatırım Talebi Alındı
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" gutterBottom>
                Yatırımınızın tamamlanması için lütfen aşağıdaki adımları takip edin:
              </Typography>
              <Box sx={{ 
                mt: 2,
                p: 3,
                borderRadius: 2,
                bgcolor: (theme) => 
                    theme.palette.mode === 'light' 
                        ? alpha(theme.palette.warning.main, 0.08)
                        : alpha(theme.palette.warning.main, 0.16),
                border: '1px solid',
                borderColor: (theme) => 
                    theme.palette.mode === 'light'
                        ? theme.palette.warning.main
                        : alpha(theme.palette.warning.main, 0.24),
            }}>
                <Typography 
                    variant="subtitle1" 
                    sx={{ 
                        color: (theme) => theme.palette.text.primary,
                        fontWeight: 500,
                        mb: 2
                    }}
                >
                    Önemli: Havale/EFT yaparken açıklama kısmına aşağıdaki kodu yazmanız gerekmektedir:
                </Typography>
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor: (theme) => 
                            theme.palette.mode === 'light' 
                                ? alpha(theme.palette.primary.main, 0.08)
                                : alpha(theme.palette.primary.main, 0.16),
                        border: '1px dashed',
                        borderColor: (theme) => theme.palette.primary.main,
                    }}
                >
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            color: (theme) => theme.palette.primary.main,
                            letterSpacing: 1
                        }}
                    >
                        {paymentDetail}
                    </Typography>
                </Paper>
            </Box>
              
              {/* Banka bilgileri ve diğer detaylar */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="body1" gutterBottom>
                  Banka Hesap Bilgileri:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Banka: Yapı Kredi Bankası
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hesap Sahibi: Kitlefon Teknoloji A.Ş.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  IBAN: TR12 3456 7890 1234 5678 9012 34
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button 
                onClick={() => setSuccessDialogOpen(false)}
                variant="outlined"
              >
                Kapat
              </Button>
              <Button 
                variant="contained"
                onClick={() => {
                  setSuccessDialogOpen(false);
                  // Yatırımlarım sayfasına yönlendirme eklenebilir
                }}
              >
                Yatırımlarıma Git
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      </Grid>
    </Container>
  );
}